
import { toast } from 'sonner';
import { TimerType, TimerSettings } from './timerTypes';
import { playNotification } from './timerUtils';

export interface TimerCompletionProps {
  timerType: TimerType;
  sequencePosition: number;
  completedPomodoros: number;
  settings: TimerSettings;
  activeTask: any | null;
  updateTaskTime: (seconds: number, completedPomodoro?: boolean) => void;
  resetTimer: (type: TimerType, startRunning: boolean, sequencePos: number) => void;
  setIsFirstStart: (value: boolean) => void;
  setSessionIndex: (index: number) => void;
  user: any | null;
  finalizeCurrentEntry?: (status: 'completed' | 'interrupted') => Promise<number | undefined>;
}

export const handleTimerCompletion = async ({
  timerType,
  sequencePosition,
  completedPomodoros,
  settings,
  activeTask,
  updateTaskTime,
  resetTimer,
  setIsFirstStart,
  setSessionIndex,
  user,
  finalizeCurrentEntry
}: TimerCompletionProps) => {
  console.log('🎯 Handling timer completion:', { timerType, sequencePosition, completedPomodoros, isAuthenticated: !!user });
  
  // Notify user
  playNotification();
  
  const currentPos = sequencePosition;
  const nextPos = currentPos + 1;
  const maxPosition = settings.sessions * 2 - 1; // Last position (long break)
  
  if (timerType === 'pomodoro') {
    let actualDuration = 0;
    
    // Finalize the current time entry if we have continuous tracking
    if (user && activeTask && finalizeCurrentEntry) {
      try {
        const duration = await finalizeCurrentEntry('completed');
        actualDuration = duration || (settings.pomodoro * 60);
        console.log('✅ Finalized time entry with duration:', actualDuration);
      } catch (error) {
        console.error('❌ Failed to finalize time entry:', error);
        actualDuration = settings.pomodoro * 60; // Fallback to expected duration
      }
    } else {
      actualDuration = settings.pomodoro * 60;
      console.log('ℹ️ Using expected duration for anonymous user or no active task');
    }
    
    // Update task time with actual tracked duration
    if (activeTask) {
      updateTaskTime(actualDuration, true);
    }
    
    const newCount = completedPomodoros + 1;
    console.log('🍅 Completed pomodoro, new count will be:', newCount);
    
    // Check if we need to move to a long break
    if (newCount % settings.sessions === 0) {
      if (user) {
        toast.success(`Completed ${settings.sessions} Pomodoro sessions!`);
      } else {
        toast.success(`Completed ${settings.sessions} Pomodoro sessions! Create an account to track your progress.`);
      }
      
      // Start long break automatically
      setTimeout(() => {
        resetTimer('longBreak', true, maxPosition);
        setIsFirstStart(false);
      }, 1000);
    } else {
      if (user) {
        toast.success('Pomodoro completed! Taking a short break.');
      } else {
        toast.success('Pomodoro completed! Taking a short break.');
      }
      
      // Start short break automatically
      const shortBreakPos = currentPos + 1;
      setTimeout(() => {
        resetTimer('shortBreak', true, shortBreakPos);
        setIsFirstStart(false);
      }, 1000);
      
      // Update current session index for the next pomodoro
      setSessionIndex(Math.floor(shortBreakPos / 2));
    }
    
    return newCount; // Return the new completed pomodoros count
  } else {
    // Break completed - finalize if we're tracking breaks
    if (user && activeTask && finalizeCurrentEntry) {
      try {
        const breakMinutes = timerType === 'shortBreak' ? settings.shortBreak : settings.longBreak;
        const expectedDuration = breakMinutes * 60;
        await finalizeCurrentEntry('completed');
        console.log('✅ Break time entry finalized');
      } catch (error) {
        console.error('❌ Failed to finalize break entry:', error);
      }
    }
    
    toast.success(`${timerType === 'shortBreak' ? 'Short' : 'Long'} break completed!`);
    
    if (timerType === 'longBreak') {
      // After long break, restart sequence if auto-repeat is enabled
      if (settings.autoRepeat) {
        setSessionIndex(0);
        setTimeout(() => {
          resetTimer('pomodoro', true, 0);
          setIsFirstStart(false);
        }, 1000);
      } else {
        // If auto-repeat is disabled, just reset to first pomodoro but don't start
        setSessionIndex(0);
        setTimeout(() => {
          resetTimer('pomodoro', false, 0);
          setIsFirstStart(true);
        }, 1000);
      }
    } else if (nextPos <= maxPosition) {
      // After short break, go to next pomodoro automatically
      const nextPomodoro = Math.floor(nextPos / 2);
      setTimeout(() => {
        resetTimer('pomodoro', true, nextPos);
        setIsFirstStart(false);
        setSessionIndex(nextPomodoro);
      }, 1000);
    }
    
    return completedPomodoros; // Return unchanged for breaks
  }
};
